import { queryWithRAG } from './ragService'

// Backend URL from environment variables
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'

// OpenAI API key stored in sessionStorage (cleared when browser closes for security)
let openaiApiKey = null

// Current AI provider (stored in localStorage for persistence)
let currentProvider = localStorage.getItem('ai_provider') || 'openai'

// Backend health status
let backendHealthy = false

// Set OpenAI API key
export function setOpenAIKey(key) {
  openaiApiKey = key
  if (key) {
    sessionStorage.setItem('openai_api_key', key)
  } else {
    sessionStorage.removeItem('openai_api_key')
  }
}

// Load OpenAI API key from sessionStorage
export function loadOpenAIKey() {
  const key = sessionStorage.getItem('openai_api_key')
  if (key) {
    openaiApiKey = key
    return true
  }
  return false
}

// Check if OpenAI API key is set
export function hasOpenAIKey() {
  return !!openaiApiKey
}

// Generate text using OpenAI API
async function generateText(prompt, options = {}) {
  if (!openaiApiKey) {
    throw new Error('OpenAI API key not set')
  }

  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${openaiApiKey}`,
    },
    body: JSON.stringify({
      model: options.model || 'gpt-3.5-turbo',
      messages: [
        {
          role: 'system',
          content: 'You are a helpful assistant that answers questions based on the user\'s notes. Be concise and accurate.',
        },
        {
          role: 'user',
          content: prompt,
        },
      ],
      temperature: options.temperature || 0.7,
      max_tokens: options.max_tokens || 500,
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error?.message || 'OpenAI API request failed')
  }

  const data = await response.json()

  if (data.choices && data.choices.length > 0) {
    return data.choices[0].message.content
  }

  return 'Sorry, I could not generate a response.'
}

// Generate text using local LLM (backend + Ollama)
async function generateTextLocal(question, notes, options = {}) {
  const response = await fetch(`${BACKEND_URL}/api/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question,
      notes,
      options: {
        model: options.model || 'llama3.2',
        temperature: options.temperature || 0.7,
        max_tokens: options.max_tokens || 500,
      },
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Local LLM request failed')
  }

  return await response.json()
}

// Answer a question using RAG
export async function answerQuestion(question, onProgress) {
  // Route based on current provider
  if (currentProvider === 'openai') {
    // OpenAI provider flow
    loadOpenAIKey()

    if (onProgress) onProgress('Finding relevant notes...', 0.2)

    const ragResult = await queryWithRAG(question)

    // If no notes found, return helpful message
    if (ragResult.noNotesFound || ragResult.relevantNotes.length === 0) {
      if (onProgress) onProgress('Complete!', 1.0)
      return {
        answer: "I don't have any notes to search through yet. Try creating some notes first, then ask me questions about them!",
        relevantNotes: [],
        question: question,
        context: '',
        provider: 'OpenAI',
      }
    }

    if (onProgress) onProgress('Generating answer with OpenAI...', 0.5)

    const answer = await generateText(ragResult.prompt, {
      max_tokens: 500,
      temperature: 0.7,
    })

    if (onProgress) onProgress('Complete!', 1.0)

    return {
      answer: answer.trim(),
      relevantNotes: ragResult.relevantNotes,
      question: ragResult.question,
      context: ragResult.context,
      provider: 'OpenAI',
    }
  } else {
    // Local LLM provider flow — use frontend RAG (semantic search) then send only relevant notes
    if (onProgress) onProgress('Finding relevant notes...', 0.2)

    const ragResult = await queryWithRAG(question)

    if (ragResult.noNotesFound || ragResult.relevantNotes.length === 0) {
      if (onProgress) onProgress('Complete!', 1.0)
      return {
        answer: "I don't have any notes to search through yet. Try creating some notes first, then ask me questions about them!",
        relevantNotes: [],
        question: question,
        context: '',
        provider: 'Local LLM',
      }
    }

    if (onProgress) onProgress('Generating answer with Local LLM...', 0.5)

    // Send only the relevant notes (already scored by semantic/keyword search)
    const relevantNoteObjects = ragResult.relevantNotes.map(item => item.note)
    const result = await generateTextLocal(question, relevantNoteObjects)

    if (onProgress) onProgress('Complete!', 1.0)

    return {
      ...result,
      relevantNotes: ragResult.relevantNotes,
      provider: 'Local LLM',
    }
  }
}

// Set AI provider (local or openai)
export function setAIProvider(provider) {
  if (!['local', 'openai'].includes(provider)) {
    throw new Error('Invalid provider. Must be "local" or "openai"')
  }
  currentProvider = provider
  localStorage.setItem('ai_provider', provider)
}

// Get current AI provider
export function getAIProvider() {
  return currentProvider
}

// Check if backend is healthy (for local LLM)
export async function checkBackendHealth() {
  try {
    const response = await fetch(`${BACKEND_URL}/api/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000)
    })
    backendHealthy = response.ok
    return backendHealthy
  } catch (error) {
    console.error('Backend health check failed:', error)
    backendHealthy = false
    return false
  }
}

// Check if AI is ready (based on provider)
export function isAIReady() {
  if (currentProvider === 'openai') {
    return hasOpenAIKey()
  } else {
    return backendHealthy
  }
}

// Get AI status (based on provider)
export function getAIStatus() {
  if (currentProvider === 'openai') {
    return {
      ready: hasOpenAIKey(),
      loading: false,
      provider: 'OpenAI',
      needsKey: !hasOpenAIKey(),
    }
  } else {
    return {
      ready: backendHealthy,
      loading: false,
      provider: 'Local LLM',
      needsKey: false,
    }
  }
}

export const AI_PROVIDERS = { LOCAL: 'local', OPENAI: 'openai' }

export default {
  generateText,
  answerQuestion,
  isAIReady,
  getAIStatus,
  setAIProvider,
  getAIProvider,
  checkBackendHealth,
  setOpenAIKey,
  loadOpenAIKey,
  hasOpenAIKey,
  AI_PROVIDERS,
}
